import React, { useEffect, useState } from 'react';

import { useGlobalContext } from '../../../context/global';
import { TypeButton } from '../../utils/button';
import { HvcLoad } from '../../utils/hooks';
import { type IPillData, PillComponent } from '../../utils/pills';
import { AlertSVG, InfoSVG } from '../../utils/svgs';

interface qType {
	key: string;
	value: any;
}

const ReportFeedbacks = () => {
	const { actions, states, rsProps } = useGlobalContext();

	const onGetReport = (query?: qType[]) => {
		actions?.get_Report({
			query: query || [],
		});
	};

	useEffect(() => {
		onGetReport();
	}, []);

	const reports = states?._report?.get_Report?.data || [];
	const isLoading = states?._report?.get_ReportLoading;

	const onNewReports = () => {
		rsProps?.callSection({
			action: 'view',
			component: 'report-issues',
			title: 'Report Issues',
		});
	};

	const viewReportDetails = (id: string) => {
		rsProps?.addRightSectionHistory();
		rsProps?.callSection({
			action: 'view',
			component: 'report-feedback-item',
			title: `Report Feedback #${id}`,
			data: {
				id,
			},
		});
	};

	const [selectedPill, setSelectedPill] = useState<string>('Unresolved');

	const pills: IPillData[] = [
		{
			filterValue: 'Unresolved',
			label: 'Pending',
		},
		{
			filterValue: 'Resolved',
			label: 'Resolved',
		},
	];

	const onClickPill = (filterValue: string) => {
		setSelectedPill(filterValue);
		onGetReport([
			{
				key: 'isResolved',
				value: filterValue === 'Resolved',
			},
		]);
	};

	return (
		<div className='f-column-33 pb-5'>
			<div className='f-row-10 w-100 text-left justify-content-between align-items-center'>
				<h3 className='header-body-text m-0'>Report Feedbacks</h3>
				<TypeButton
					title='Report New Issue'
					buttonSize='little'
					buttonType='outlined'
					onClick={onNewReports}
				/>
			</div>
			<div className='f-row-7 align-items-center'>
				<InfoSVG
					width='16'
					height='16'
					color='var(--neutral-500)'
				/>
				<p className='m-0 text-little color-label'>View feedback for all reported issues</p>
			</div>
			<div>
				<PillComponent
					filterValues={[selectedPill]}
					pills={pills}
					onClickPill={onClickPill}
				/>
			</div>
			<HvcLoad
				removeDOM
				load={isLoading}
				view
				className='f-column-13'
			>
				{reports.length === 0 && !isLoading ? (
					<div className='report-empty-state'>
						<div className='report-empty-icon'>
							<AlertSVG />
						</div>
						<h4 className='m-0 mb-2'>No Reports Found</h4>
						<p className='m-0 text-little color-label'>
							{selectedPill === 'Resolved' ? 'No resolved reports yet' : 'No pending reports at the moment'}
						</p>
					</div>
				) : (
					reports.map((i, index) => (
						<ReportFeedbackItem
							key={index}
							value={i.report.title}
							onClick={() => viewReportDetails(i.id)}
							status={i.isResolved}
							createdAt={i.createdAt}
						/>
					))
				)}
			</HvcLoad>
		</div>
	);
};

export default ReportFeedbacks;

const ReportFeedbackItem = ({
	value,
	onClick,
	status,
	createdAt,
}: {
	value: string;
	createdAt: string;
	onClick: () => void;
	status: boolean;
}) => (
	<div
		className='f-column-15 report-feedback-card'
		onClick={onClick}
		role='button'
		tabIndex={0}
		onKeyDown={(e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				onClick();
			}
		}}
	>
		<div className='f-row justify-content-between align-items-center'>
			<p className='m-0 font-16'>{value}</p>
			<span className={`report-status-badge ${status ? 'Resolved' : 'Pending'}`}>{status ? 'Resolved' : 'Pending'}</span>
		</div>
		<p className='m-0 font-11 color-label'>{new Date(createdAt).toDateString()}</p>
	</div>
);
